// src/utils/questionUtils.js

/**
 * Validates and normalizes range values based on question options
 * @param {Object} value The range value to validate {min: number, max: number}
 * @param {Object} options The question options {min: number, max: number, step: number}
 * @returns {Object} Normalized range value
 * @throws {Error} If validation fails
 */


export const validateRangeValue = (value, options) => {
    const {
      min: optionMin = 0,
      max: optionMax = 100,
      step = 1
    } = options || {};
  
    if (!value || typeof value !== 'object') {
      throw new Error('Range value must be an object with min and max properties');
    }
  
    let { min, max } = value;
  
    // Convert to numbers
    min = Number(min);
    max = Number(max);
  
    // Validate numbers
    if (isNaN(min) || isNaN(max)) {
      throw new Error('Range values must be numbers');
    }
  
    // Validate range
    if (min < optionMin || min > optionMax) {
      throw new Error(`Minimum value must be between ${optionMin} and ${optionMax}`);
    }
    if (max < optionMin || max > optionMax) {
      throw new Error(`Maximum value must be between ${optionMin} and ${optionMax}`);
    }
    if (min > max) {
      throw new Error('Minimum value cannot be greater than maximum value');
    }
  
    // Normalize to step
    const normalizedMin = Math.round(min / step) * step;
    const normalizedMax = Math.round(max / step) * step;
  
    return {
      min: normalizedMin,
      max: normalizedMax
    };
  };
  

  // utils/questionUtils.js

// Keep existing functions like areResponsesDifferent

export const formatResponse = (question, response) => {
  if (!response || response.response_value === undefined) {
      return 'Not answered';
  }

  const value = response.response_value;

  switch (question.question_type) {
      case 'range':
          try {
              let rangeValue;
              if (typeof value === 'string') {
                  rangeValue = JSON.parse(value);
              } else if (typeof value === 'object') {
                  rangeValue = value;
              } else {
                  return 'Invalid range value';
              }
              
              if (!rangeValue || typeof rangeValue !== 'object') {
                  return 'Invalid range value';
              }
              
              const symbol = rangeValue.currency || '$';
              const min = Number(rangeValue.min).toLocaleString() || '0';
              const max = Number(rangeValue.max).toLocaleString() || '0';
              
              return `${symbol}${min} - ${symbol}${max}`;
          } catch (e) {
              console.error('Error formatting range value:', e, value);
              return 'Invalid range value';
          }
          
      case 'multiple_choice':
          try {
              const choices = Array.isArray(value) ? value : JSON.parse(value);
              return choices.join(', ');
          } catch (e) {
              return value?.toString() || 'Not answered';
          }
      
      case 'boolean':
          return value === true || value === 'true' ? 'Yes' : 
                 value === false || value === 'false' ? 'No' : 
                 'Not answered';
      
      case 'tristate_boolean':
          const tristateMap = {
              'YES': 'Yes',
              'NO': 'No',
              'DONT_KNOW': "Don't Know"
          };
          return tristateMap[value] || value?.toString() || 'Not answered';
      
      default:
          return String(value || 'Not answered');
  }
};

// Optional: Add a helper for organization response display
export const renderOrganizationResponse = (question, orgResponse, formatResponse) => {
  if (!orgResponse?.response_value) return null;

  return {
      formattedValue: formatResponse(question, {
          response_value: orgResponse.response_value
      }),
      setBy: orgResponse.locked_to_user_name
  };
};


  /**
   * Gets default value for a question based on its type and options
   * @param {string} type Question type
   * @param {Object} options Question options
   * @returns {any} Default value for the question type
   */
  export const getDefaultValue = (type, options) => {
    switch (type) {
      case 'multiple_choice':
            return [];
        case 'tristate_boolean':
            return null;  // No default selection for tristate
        case 'boolean':
            return null;  // No default selection for boolean
        case 'range':
            return null;  // No default range
        case 'text':
            return '';
        default:
            return null;
    }
  };
  
  // src/utils/questionUtils.js

// Add this function to your existing questionUtils.js
export const areResponsesDifferent = (responseA, responseB, questionType) => {
  if (responseA === responseB) return false;
  if (!responseA || !responseB) return true;

  try {
      switch (questionType) {
          case 'multiple_choice': {
              const arrA = Array.isArray(responseA) ? responseA : 
                  typeof responseA === 'string' ? JSON.parse(responseA) : [responseA];
              const arrB = Array.isArray(responseB) ? responseB :
                  typeof responseB === 'string' ? JSON.parse(responseB) : [responseB];
              return JSON.stringify(arrA.sort()) !== JSON.stringify(arrB.sort());
          }

          case 'range': {
              const rangeA = typeof responseA === 'object' ? responseA : JSON.parse(responseA);
              const rangeB = typeof responseB === 'object' ? responseB : JSON.parse(responseB);
              return rangeA.min !== rangeB.min || rangeA.max !== rangeB.max;
          }

          case 'tristate_boolean':
              return String(responseA).toUpperCase() !== String(responseB).toUpperCase();

          case 'boolean':
              return Boolean(responseA) !== Boolean(responseB);

          default:
              return String(responseA) !== String(responseB);
      }
  } catch (error) {
      console.error('Error comparing responses:', error);
      return true; // If we can't compare properly, assume they're different
  }
};

  /**
   * Validates a response value against question type and options
   * @param {any} value Response value
   * @param {string} type Question type
   * @param {Object} options Question options
   * @returns {boolean} True if valid
   */
  export const validateResponse = (value, type, options) => {
    try {
      switch (type) {
        case 'multiple_choice':
          if (!Array.isArray(value)) return false;
          if (!options?.choices) return false;
          return value.every(v => options.choices.includes(v));
          
        case 'boolean':
          return typeof value === 'boolean';
          
        case 'range':
          if (!value || typeof value !== 'object') return false;
          validateRangeValue(value, options);
          return true;
          
        default:
          return true;
      }
    } catch (error) {
      console.warn('Response validation failed:', error);
      return false;
    }
  };
  
  /**
   * Formats a response value for display
   * @param {any} value Response value
   * @param {string} type Question type
   * @param {Object} options Question options
   * @returns {string} Formatted value
   */
  export const formatResponseValue = (value, type, options) => {
    switch (type) {
      case 'multiple_choice':
        if (!Array.isArray(value)) return '';
        return value.join(', ');
        
      case 'boolean':
        return value ? 'Yes' : 'No';
        
      case 'range':
        if (!value || typeof value !== 'object') return '';
        const prefix = options?.prefix || '';
        return `${prefix}${value.min} - ${prefix}${value.max}`;
        
      default:
        return String(value || '');
    }
  };
  
  /**
   * Checks if a response is complete based on question requirements
   * @param {any} value Response value
   * @param {Object} question Question object
   * @returns {boolean} True if response is complete
   */
  // In utils/questionUtils.js
export const isResponseComplete = (value, question) => {
  if (!value && value !== false) return false;  // Allow explicit false values

  switch (question.question_type) {
      case 'tristate_boolean':
          // Valid values are 'YES', 'NO', or 'DONT_KNOW'
          return ['YES', 'NO', 'DONT_KNOW'].includes(value);

      case 'boolean':
          // Accept explicit boolean values
          return value === true || value === false;

      case 'multiple_choice':
          try {
              const choices = Array.isArray(value) ? value : JSON.parse(value);
              return Array.isArray(choices) && choices.length > 0;
          } catch {
              return false;
          }

      case 'range':
          try {
              const rangeValue = typeof value === 'string' ? JSON.parse(value) : value;
              return (
                  rangeValue &&
                  typeof rangeValue === 'object' &&
                  'min' in rangeValue &&
                  'max' in rangeValue &&
                  rangeValue.min !== null &&
                  rangeValue.max !== null
              );
          } catch {
              return false;
          }

      case 'text':
          return Boolean(value && value.trim());

      default:
          return Boolean(value);
  }
};